import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Verification Badge Component
 * แสดงสถานะการตรวจสอบร้านค้า
 * 
 * @param {object} verification - ข้อมูลการตรวจสอบ
 * @param {string} verification.status - สถานะ (unverified, pending, in_review, approved, rejected, expired)
 * @param {number} verification.verificationScore - คะแนนการตรวจสอบ (0-175)
 * @param {number} verification.riskScore - Risk Score (0-100)
 * @param {string} size - ขนาด badge (sm, md, lg)
 */
const VerificationBadge = ({ verification, size = 'md' }) => {
  const { t } = useTranslation();
  
  if (!verification) {
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
        size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-sm' : 'text-xs'
      } bg-gray-100 text-gray-700`}>
        ⚠️ {t('verification.unverified')}
      </span>
    );
  }
  
  const status = verification.status || 'unverified';
  const riskScore = verification.riskScore || 100;
  
  // Badge colors based on status
  const getStatusBadge = () => {
    switch (status) {
      case 'approved':
        return {
          bg: 'bg-green-100',
          text: 'text-green-800',
          icon: '✅',
          label: t('verification.verified'),
        };
      case 'pending':
        return {
          bg: 'bg-yellow-100',
          text: 'text-yellow-800',
          icon: '⏳',
          label: t('verification.pending'),
        };
      case 'in_review':
        return {
          bg: 'bg-blue-100',
          text: 'text-blue-800',
          icon: '🔍',
          label: t('verification.inReview'),
        };
      case 'rejected':
        return {
          bg: 'bg-red-100',
          text: 'text-red-800',
          icon: '❌',
          label: t('verification.rejected'),
        };
      case 'expired':
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-800',
          icon: '⏰',
          label: t('verification.expired'),
        };
      default:
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-700',
          icon: '⚠️',
          label: t('verification.unverified'),
        };
    }
  };
  
  const statusBadge = getStatusBadge();
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-xs px-2.5 py-1',
    lg: 'text-sm px-3 py-1.5',
  };
  
  return (
    <span className={`inline-flex items-center rounded-full font-semibold ${sizeClasses[size]} ${statusBadge.bg} ${statusBadge.text}`}>
      {statusBadge.icon} {statusBadge.label}
    </span>
  );
};

/**
 * Risk Score Badge Component
 * แสดง Risk Score (0-100)
 * 
 * @param {number} riskScore - Risk Score (0-100, 100 = ปลอดภัย)
 * @param {string} size - ขนาด badge (sm, md, lg)
 */
export const RiskScoreBadge = ({ riskScore, size = 'md' }) => {
  const { t } = useTranslation();
  
  if (riskScore === null || riskScore === undefined) {
    return null;
  }
  
  // Determine risk level
  let riskLevel, bgColor, textColor, icon;
  if (riskScore >= 80) {
    riskLevel = 'low';
    bgColor = 'bg-green-100';
    textColor = 'text-green-800';
    icon = '🟢';
  } else if (riskScore >= 50) {
    riskLevel = 'medium';
    bgColor = 'bg-yellow-100';
    textColor = 'text-yellow-800';
    icon = '🟡';
  } else {
    riskLevel = 'high';
    bgColor = 'bg-red-100';
    textColor = 'text-red-800';
    icon = '🔴';
  }
  
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-xs px-2.5 py-1',
    lg: 'text-sm px-3 py-1.5',
  };
  
  return (
    <span className={`inline-flex items-center rounded-full font-semibold ${sizeClasses[size]} ${bgColor} ${textColor}`} title={t('verification.riskScore')}>
      {icon} {riskScore}/100
    </span>
  );
};

export default VerificationBadge;






